import { createSignal, onCleanup } from "solid-js";
import { AudioRecorder } from "../utils/audioRecorder";
import { SessionRecording, SessionState } from "../types/SessionState";

type RecordingStatus = "recording" | "paused" | "completed" | "failed" | null;

export function useRecording(sessionId: string) {
  const [recorder, setRecorder] = createSignal<AudioRecorder | null>(null);
  const [status, setStatus] = createSignal<RecordingStatus>(null);
  const [duration, setDuration] = createSignal(0);
  const [recordings, setRecordings] = createSignal<SessionRecording[]>([]);
  let durationInterval: number | null = null;

  const startRecording = async (): Promise<boolean> => {
    const newRecorder = new AudioRecorder(sessionId, (newStatus) => {
      setStatus(newStatus as RecordingStatus);
    });
    const success = await newRecorder.start();

    if (success) {
      setRecorder(newRecorder);
      setDuration(0);
      // Update duration every second
      durationInterval = window.setInterval(() => {
        setDuration((d) => d + 1);
      }, 1000);
    }

    return success;
  };

  const stopRecording = async (): Promise<void> => {
    await recorder()?.stop();
    if (durationInterval) {
      clearInterval(durationInterval);
      durationInterval = null;
    }
    setRecorder(null);
    setDuration(0);
  };

  const pauseRecording = async (): Promise<void> => {
    await recorder()?.pause();
    if (durationInterval) {
      clearInterval(durationInterval);
      durationInterval = null;
    }
  };

  const resumeRecording = async (): Promise<void> => {
    await recorder()?.resume();
    durationInterval = window.setInterval(() => {
      setDuration((d) => d + 1);
    }, 1000);
  };

  const addRecording = (recording: SessionRecording) => {
    setRecordings((prev) => [...prev, recording]);
  };

  const updateTranscriptionState = (state: SessionState) => {
    recorder()?.updateTranscriptionState(state);
  };

  // Cleanup on component unmount
  onCleanup(() => {
    stopRecording().catch(console.error);
  });

  return {
    status,
    duration,
    recordings,
    isRecording: () => recorder()?.isRecording() || false,
    isPaused: () => recorder()?.isPaused() || false,
    startRecording,
    stopRecording,
    pauseRecording,
    resumeRecording,
    addRecording,
    updateTranscriptionState,
  };
}
